const FIREBASE_TOKEN_REVOKED_ERRORS = new Set([
  "auth/user-token-expired",
  "auth/invalid-user-token",
  "auth/user-disabled",
]);

export default defineNuxtPlugin(async (nuxtApp) => {
  const authStore = useAuthStore();
  const hasSession = await authStore.initSession();

  // no server session but firebase auth may still be restored from indexeddb —
  // sign it out so firestore reads don't fail with permission-denied during middleware
  if (!hasSession) {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    await auth.authStateReady();
    if (auth.currentUser) await authStore.forceSignOut();
  }

  const checkFirebaseTokenValidity = async () => {
    const { getAuth } = await import("firebase/auth");
    const user = getAuth().currentUser;
    if (!user) return;

    try {
      await user.getIdToken(true);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? "";
      if (FIREBASE_TOKEN_REVOKED_ERRORS.has(code)) {
        await authStore.forceSignOut();
      }
    }
  };

  // Intercept all $fetch calls — on 401, immediately check if the Firebase
  // refresh token was revoked server-side instead of waiting for the next
  // visibility-change or interval tick. on MFA_VERIFICATION_REQUIRED, the
  // server-side gate has rejected a pre-MFA token so we force a fresh login.
  globalThis.$fetch = $fetch.create({
    onResponseError: async ({ response }) => {
      if (response.status === 401) {
        await checkFirebaseTokenValidity();
        return;
      }
      if (
        response.status === 403 &&
        response._data?.data?.code === "MFA_VERIFICATION_REQUIRED"
      ) {
        await authStore.forceSignOut();
      }
    },
  }) as typeof $fetch;

  nuxtApp.hook("app:mounted", () => {
    watch(
      () => authStore.sessionRevoked,
      (revoked) => {
        if (revoked) navigateTo("/login");
      },
    );

    // check when user switches back to this tab
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkFirebaseTokenValidity();
    });

    // background fallback every 2 minutes
    setInterval(checkFirebaseTokenValidity, 2 * 60 * 1000);
  });
});
